'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { AgentChatMessage, AgentToolEvent, AgentBuildOp } from '@/lib/types';
import api from '@/lib/api';
import { useToast } from '../Toast';
import { useConfirm } from '../ConfirmDialog';
import LlmPicker, { useLlmChoice } from '../LlmPicker';

// Tool names whose execution mutates the bit library / permutation lists.
// Exported so other agentChat callers (e.g. the cast workflow) share the same list.
export const LIBRARY_WRITE_TOOLS = ['add_bit', 'update_bit', 'generate_bits', 'add_list_item'];

interface ChatEntry {
  role: 'user' | 'assistant';
  content: string;
  toolEvents?: AgentToolEvent[];
}

interface AgentChatPanelProps {
  getBuildState: () => string;
  onBuildOps: (ops: AgentBuildOp[]) => void;
  onLibraryChanged: () => void;
}

// Human-readable summary per tool; full args/result stay available in the chip title
function toolEventChip(event: AgentToolEvent): string {
  const args = event.args || {};
  const arg = (key: string) => {
    const v = args[key];
    return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
  };

  let label: string;
  switch (event.tool) {
    case 'add_bit':
      label = `+ "${arg('label')}" → ${arg('category_slug')}`;
      break;
    case 'generate_bits':
      label = `⚡ ${Number(args.count) || 5} bits → ${arg('category_slug')}`;
      break;
    case 'update_bit':
      label = `edited bit ${arg('bit_id')}`;
      break;
    case 'variate_bit':
      label = `variations for bit ${arg('bit_id')}`;
      break;
    case 'add_list_item':
      label = `+ "${arg('value')}" → ${arg('list_name')}`;
      break;
    case 'enhance_prompt':
      label = 'enhanced prompt';
      break;
    default:
      label = event.tool.startsWith('list_') ? `read ${event.tool}` : event.tool;
  }
  return label.length > 60 ? `${label.slice(0, 60)}…` : label;
}

export default function AgentChatPanel({ getBuildState, onBuildOps, onLibraryChanged }: AgentChatPanelProps) {
  const [messages, setMessages] = useState<ChatEntry[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // Per-message index of the expanded tool chip (one expanded chip per message)
  const [expandedTools, setExpandedTools] = useState<Record<number, number | null>>({});

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const toast = useToast();
  const { confirm } = useConfirm();

  const {
    choice: llmChoice,
    resolved: resolvedLlm,
    change: handleLlmChange,
    confirmGpu: confirmGpuFor,
  } = useLlmChoice('builder-agent-llm');

  // Auto-scroll to bottom on new messages / spinner / error bubble
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isSending, sendError]);

  const sendHistory = useCallback(async (history: ChatEntry[]) => {
    setIsSending(true);
    setSendError(null);
    try {
      const apiMessages: AgentChatMessage[] = history.map(m => ({ role: m.role, content: m.content }));
      const response = await api.agentChat({
        messages: apiMessages,
        build_state: getBuildState(),
        llm: resolvedLlm,
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.message,
        toolEvents: response.tool_events,
      }]);

      if (response.build_ops.length > 0) {
        onBuildOps(response.build_ops);
      }
      if (response.tool_events.some(ev => LIBRARY_WRITE_TOOLS.includes(ev.tool))) {
        onLibraryChanged();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Agent request failed';
      setSendError(message);
      toast.error(`Agent error: ${message}`);
    } finally {
      setIsSending(false);
      // Defer until after re-render so the (still disabled) textarea can take focus
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [getBuildState, onBuildOps, onLibraryChanged, toast, resolvedLlm]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isSending) return;
    if (!(await confirmGpuFor(resolvedLlm))) return; // covers auto-resolution too

    const userMessage: ChatEntry = { role: 'user', content: text };
    const history = [...messages, userMessage];
    setMessages(history);
    setInput('');
    sendHistory(history);
  };

  const handleRetry = () => {
    if (isSending || messages.length === 0) return;
    // Last user message is already in local history — just resend it
    sendHistory(messages);
  };

  const handleClear = async () => {
    if (messages.length === 0 && !sendError) return;
    const confirmed = await confirm({
      title: 'Clear Conversation',
      message: 'Clear the agent conversation? The prompt and library are not affected.',
      confirmText: 'Clear',
      variant: 'warning',
    });
    if (!confirmed) return;
    setMessages([]);
    setSendError(null);
    setExpandedTools({});
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-subtle)]">
        <h3 className="text-sm font-medium">Agent</h3>
        <div className="flex items-center gap-2">
          <LlmPicker
            choice={llmChoice}
            resolved={resolvedLlm}
            onChange={handleLlmChange}
            title="Agent model — Auto uses the uncensored Dolphin in private mode, Qwen otherwise"
          />
        <button
          onClick={handleClear}
          disabled={messages.length === 0 && !sendError}
          className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          title="Clear conversation"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        </button>
        </div>
      </div>

      {/* Message thread */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && !isSending && !sendError && (
          <div className="h-full flex items-center justify-center">
            <div className="text-center px-4 space-y-2">
              <p className="text-sm text-[var(--text-secondary)]">
                Ask the agent to work on your prompt and library.
              </p>
              <p className="text-xs text-[var(--text-muted)]">
                It can edit prompt sections, add or generate bits in any category,
                and work with permutation lists — e.g. &quot;make the scene moodier
                and add three grittier lighting bits&quot;.
              </p>
            </div>
          </div>
        )}

        {messages.map((msg, i) => {
          const expandedIdx = expandedTools[i];
          const expandedEvent =
            msg.toolEvents && expandedIdx != null ? msg.toolEvents[expandedIdx] : undefined;
          return (
            <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
              <div
                className={`max-w-[90%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                  msg.role === 'user'
                    ? 'bg-[var(--accent)] text-black'
                    : 'bg-[var(--bg-elevated)] border border-[var(--border-subtle)] text-[var(--text-primary)]'
                }`}
              >
                {msg.content}
              </div>
              {msg.toolEvents && msg.toolEvents.length > 0 && (
                <div className="mt-1.5 max-w-[90%]">
                  <div className="flex flex-wrap gap-1">
                    {msg.toolEvents.map((event, j) => (
                      <button
                        key={j}
                        onClick={() =>
                          setExpandedTools(prev => ({ ...prev, [i]: prev[i] === j ? null : j }))
                        }
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-[var(--bg-hover)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-primary)] transition-colors cursor-pointer"
                        title={event.tool}
                      >
                        <span aria-hidden="true">{expandedIdx === j ? '▾' : '▸'}</span>
                        {toolEventChip(event)}
                      </button>
                    ))}
                  </div>
                  {expandedEvent && (
                    <div className="mt-1 rounded border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-2 font-mono text-[10px] text-[var(--text-secondary)] whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                      <div className="font-medium text-[var(--text-primary)]">{expandedEvent.tool}</div>
                      <div className="mt-1 text-[var(--text-muted)]">args</div>
                      <div>{JSON.stringify(expandedEvent.args ?? {}, null, 2)}</div>
                      <div className="mt-1 text-[var(--text-muted)]">result</div>
                      <div>{expandedEvent.result}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {isSending && (
          <div className="flex items-start">
            <div className="bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg px-3 py-2">
              <svg className="w-4 h-4 animate-spin text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          </div>
        )}

        {sendError && !isSending && (
          <div className="flex flex-col items-start gap-1.5">
            <div className="max-w-[90%] rounded-lg px-3 py-2 text-sm bg-[var(--error-soft)] border border-[var(--error)] text-[var(--error)] break-words">
              {sendError}
            </div>
            <button
              onClick={handleRetry}
              className="px-2 py-1 text-xs font-medium rounded bg-[var(--bg-hover)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors cursor-pointer"
            >
              Retry
            </button>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-[var(--border-subtle)]">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            className="input text-sm resize-none"
            rows={2}
            placeholder="Ask the agent…"
            disabled={isSending}
          />
          <button
            onClick={handleSend}
            disabled={isSending || !input.trim()}
            className="btn btn-primary px-3 py-2 shrink-0"
            title="Send (Enter)"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
